import React, { useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import MainWrapper from "../../MainWrapper";
import "../../../assets/css/Main.css";
import { connect } from "react-redux";
import Loader from "../../layouts/Loader";
import { toast } from "react-toastify";
import { USER_MENU_LIST_REQUEST } from "../../../_utils/constants/admin/Admin";
import { WIDGET_COMPONENTS } from "./widgets";
import "./Dashboard.css";

// Preferred on-screen arrangement. Each inner array is a horizontal row of cells
// laid out side by side (centered). A cell is either a widget id (string) or a
// vertical stack of ids (nested array) rendered stacked in one column. Only the
// role's selected + registered widgets render; empty cells/rows are skipped, and
// any selected widget not listed here is appended as its own row so new widgets
// are never dropped.
const DASHBOARD_LAYOUT = [
  ["potential-lost-customers", "sales-rso-forecast", "estimate-ready", "ar-over-90", "inventory-over-90", "not-shipped-invoices"],
  // The two outstanding-work circles read as a pair, so they share a row.
  ["unshipped-jobs", "non-shipped-labor"],
  // Customer service: Order Desk missed calls from RingCentral, stepping back week by week.
  ["order-desk-missed-calls"],
  [ "written-sales"],
  ["tiered-customer-performance"],
  ["non-tiered-customer-performance"],
  ["new-customers-secured"],
  // "Estimating Turnaround" is now a full-width per-salesperson matrix, so it
  // gets its own row instead of being stacked as a small card.
  ["estimating-turnaround"],
  ["revenue-profit"],
  // Warehouse receiving: trucks received and average put-away time, with a trend.
  ["receiving-time"],
  ["insufficient-materials"],
];

// Widgets that should render full page width, left-aligned (e.g. wide data
// tables) instead of being centered like the cards/KPI widgets.
const FULL_WIDTH_WIDGETS = new Set([
  "insufficient-materials",
  "receiving-time",
  "written-sales",
  "revenue-profit",
  "estimating-turnaround",
]);

// Role-driven dashboard orchestrator.
//
// The set of widgets a user sees is configured per Role (in the Role admin
// modal's "Dashboard" section) and stored in MS_Role.PermissionObject. We read
// that config via the same role-permission fetch the Sidebar uses
// (USER_MENU_LIST_REQUEST -> Admin.userMenuList[0]), then render the selected
// widgets from the widget registry.
//
// Each widget is passed `dataScope` ("all" | "self") and `userId` so it can
// optionally filter its data to the logged-in user (used by the Sales role).
const Dashboard = ({
  auth: { auth, isAuthenticated },
  Admin: { userMenuList, isLoading },
  fetchUserMenuList,
}) => {
  useEffect(() => {
    // One-time login-success toast (preserved from the original dashboard).
    let isLoggedIn = JSON.parse(localStorage.getItem("isLoggedIn"));
    if (isAuthenticated && isLoggedIn) {
      toast.success("Login success.");
      localStorage.setItem("isLoggedIn", false);
    }
    if (auth[0]?.token && auth[0]?.roleid && auth[0]?.userId) {
      fetchUserMenuList(auth[0].token, auth[0].roleid, auth[0].userId);
    }
  }, [auth, isAuthenticated]);

  // Resolve this role's dashboard config from the role-permission payload.
  const { widgetIds, dataScope } = useMemo(() => {
    const dashboard =
      userMenuList && userMenuList[0] ? userMenuList[0].dashboard : null;
    const configured =
      dashboard && Array.isArray(dashboard.widgets) ? dashboard.widgets : [];

    return {
      // The role's checked widgets are authoritative: render exactly the widgets
      // selected in the role's Dashboard config. None selected => empty dashboard.
      widgetIds: configured,
      dataScope: (dashboard && dashboard.dataScope) || "all",
    };
  }, [userMenuList]);

  if (!isAuthenticated) return <Navigate to="/login" />;

  const userId = auth[0]?.userId;

  // Arrange the role's selected widgets into the preferred layout rows; drop
  // empty rows and append any selected-but-unlisted widgets as their own rows.
  let selected = widgetIds.filter((id) => WIDGET_COMPONENTS[id]);
  // Preview the new "Written Sales" widget even if this role's dashboard config
  // hasn't opted in yet. De-duped so it never yields a duplicate React key when
  // the role already selected it. (Remove once roles configure it explicitly.)
  // if (!selected.includes("written-sales")) selected.push("written-sales");
  // Normalize every cell to a vertical stack (array) of selected ids, then drop
  // empty cells and empty rows. Unlisted-but-selected widgets get their own row.
  const toStack = (cell) =>
    (Array.isArray(cell) ? cell : [cell]).filter((id) => selected.includes(id));
  const rows = DASHBOARD_LAYOUT
    .map((row) => row.map(toStack).filter((stack) => stack.length > 0))
    .filter((row) => row.length > 0);
  const placed = new Set(rows.flat(2));
  selected.filter((id) => !placed.has(id)).forEach((id) => rows.push([[id]]));
  return (
    <MainWrapper>
      <Loader loading={isLoading}>
        <div className="dashboard-grid">
          {rows.map((row, i) => {
            // A row of exactly two widgets splits the page into two equal
            // halves so the pair fills the full width instead of huddling
            // in the center (e.g. New Customers Secured + Estimating Turnaround).
            const rowClass =
              row.length === 2
                ? "dashboard-row dashboard-row--split"
                : "dashboard-row";
            return (
              <div className={rowClass} key={i}>
              {row.map((stack, j) => {
                const isFull =
                  stack.length === 1 && FULL_WIDTH_WIDGETS.has(stack[0]);
                const cellClass = isFull
                  ? "dashboard-cell dashboard-cell--full"
                  : "dashboard-cell";
                return (
                  <div className={cellClass} key={j}>
                    {stack.map((id) => {
                      const Widget = WIDGET_COMPONENTS[id];
                      return (
                        <Widget key={id} dataScope={dataScope} userId={userId} />
                      );
                    })}
                  </div>
                );
              })}
              </div>
            );
          })}
        </div>
      </Loader>
    </MainWrapper>
  );
};

const mapStateToProps = ({ auth, Admin }) => {
  return {
    auth,
    Admin,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchUserMenuList: (token, roleId, userId) =>
      dispatch({ type: USER_MENU_LIST_REQUEST, token, roleId, userId }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
